import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { SectionHeader } from '@/components/dashboard/MetricCard';
import { Segmented, Loading } from '@/components/dashboard/kit';
import { dashboardApi } from '@/services/dashboardApi';
import AdminSummary from '@/components/dashboard/admin/AdminSummary';
import PeopleSection, { PEOPLE_SUBS } from '@/components/dashboard/admin/PeopleSection';
import EventsManager from '@/components/dashboard/admin/EventsManager';
import BootcampManager from '@/components/dashboard/admin/BootcampManager';
import AcademyManager from '@/components/dashboard/admin/AcademyManager';
import ResourcesManager from '@/components/dashboard/admin/ResourcesManager';
import OpportunityManager from '@/components/dashboard/admin/OpportunityManager';
import CopyEditor from '@/components/dashboard/admin/CopyEditor';
import CdmPanel from '@/components/dashboard/admin/CdmPanel';

/*
  Panel de administración (decisión de Samuel, 10/10/2026). Cinco secciones con sub-pestañas:
    Resumen · Personas (Todas, Usuarios, Test de idoneidad, Inscripciones) · Eventos (Eventos, Bootcamps)
    · Contenido (Academia, Recursos, Oportunidades, Textos) · Dirección (CDM, solo superadmin).
  La URL lo dice todo para poder enlazar: ?tab=personas&sub=inscripciones&event=event:slug&id=email.
  El dinero es solo del superadmin: el admin no ve Dirección ni cifras económicas (el servidor no se las manda).
  Los enlaces antiguos (?tab=Usuarios, Leads, Funnel, Brokers, Red, Ventas, Comisiones, Eventos, Bootcamp,
  Academia, Recursos, Oportunidades, Textos, CDM…) redirigen a su sitio nuevo.
*/
const SECTIONS = [
  { key: 'resumen', label: 'Resumen' },
  { key: 'personas', label: 'Personas' },
  { key: 'eventos', label: 'Eventos', subs: [['eventos', 'Eventos'], ['bootcamps', 'Bootcamps']] },
  { key: 'contenido', label: 'Contenido', subs: [['academia', 'Academia'], ['recursos', 'Recursos'], ['oportunidades', 'Oportunidades'], ['textos', 'Textos']] },
  { key: 'direccion', label: 'Dirección', superadmin: true },
];
const LEGACY = {
  Resumen: ['resumen'], Usuarios: ['personas', 'usuarios'], Brokers: ['personas', 'usuarios'], Red: ['personas', 'red'],
  Leads: ['personas', 'tests'], Funnel: ['personas', 'tests'], Eventos: ['eventos', 'eventos'], Bootcamp: ['eventos', 'bootcamps'],
  Academia: ['contenido', 'academia'], Recursos: ['contenido', 'recursos'], Oportunidades: ['contenido', 'oportunidades'],
  Textos: ['contenido', 'textos'], CDM: ['direccion'], 'Dirección (CDM)': ['direccion'],
};

// Sección y sub-pestaña reales a partir de la URL. sa = null mientras no se sabe si es superadmin.
function resolve(params, sa) {
  const raw = params.get('tab');
  let tab = raw || 'resumen';
  let sub = params.get('sub');
  if (raw === 'Ventas' || raw === 'Comisiones') {  // antes «Ventas y comisiones»: ahora solo en Dirección
    if (sa === null) return { pending: true };
    if (sa) { tab = 'direccion'; sub = raw === 'Comisiones' || params.get('vista') === 'comisiones' ? 'comisiones' : 'miembros'; } else { tab = 'resumen'; sub = null; }
  } else if (LEGACY[raw]) {
    [tab, sub] = LEGACY[raw];
  }
  const section = SECTIONS.find((s) => s.key === tab) || SECTIONS[0];
  if (section.superadmin && sa === null) return { pending: true };
  if (section.superadmin && !sa) return { tab: 'resumen', sub: null };
  if (section.key === 'personas' && !PEOPLE_SUBS.some(([k]) => k === sub)) sub = 'todas';
  if (section.subs && !section.subs.some(([k]) => k === sub)) sub = section.subs[0][0];
  return { tab: section.key, sub: section.key === 'resumen' ? null : sub };
}

export default function DashboardAdmin() {
  const [params, setParams] = useSearchParams();
  // null = aún no sabemos si es superadmin (evita el parpadeo de la pestaña Dirección)
  const [superadmin, setSuperadmin] = useState(null);
  const [staging, setStaging] = useState(false);
  useEffect(() => {
    dashboardApi.superadminMe()
      .then((r) => { setSuperadmin(!!r?.superadmin); setStaging(!!r?.staging); })
      .catch(() => setSuperadmin(false));
  }, []);

  const { tab, sub, pending } = resolve(params, superadmin);
  const personKey = params.get('id');
  const eventKey = params.get('event') || '';

  // Navegación: siempre por la URL (se puede enlazar y el botón Atrás funciona)
  const go = useCallback((t, extra = {}) => {
    const p = {};
    if (t !== 'resumen') p.tab = t;
    Object.entries(extra).forEach(([k, v]) => { if (v) p[k] = v; });
    setParams(p);
  }, [setParams]);
  const patch = useCallback((changes) => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      Object.entries(changes).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
      return p;
    }, { replace: true });
  }, [setParams]);

  // Enlaces antiguos → dirección nueva (una vez sabemos el rol)
  useEffect(() => {
    if (pending) return;
    const want = { tab: tab === 'resumen' ? null : tab, sub };
    if ((params.get('tab') || null) !== want.tab || (params.get('sub') || null) !== (want.sub || null) || params.get('vista')) {
      patch({ tab: want.tab, sub: want.sub, vista: null });
    }
  }, [pending, tab, sub, params, patch]);

  const openPerson = useCallback((key) => patch({ id: key }), [patch]);
  const closePerson = useCallback(() => patch({ id: null }), [patch]);
  const goPeople = useCallback((extra) => go('personas', extra), [go]);
  const section = SECTIONS.find((s) => s.key === tab) || SECTIONS[0];
  const visible = SECTIONS.filter((s) => !s.superadmin || superadmin);

  return (
    <div className="space-y-6" data-testid="dashboard-admin">
      <SectionHeader
        title="Panel de administración"
        subtitle="Personas, eventos y contenido del club"
        action={
          <span className="hidden sm:flex items-center gap-1.5 text-[11px] text-gold uppercase tracking-wider font-semibold bg-gold/[0.06] border border-gold/15 px-3 py-1.5 rounded-full">
            <ShieldCheck size={13} /> {superadmin ? 'Superadmin' : 'Administrador'}
          </span>
        }
      />

      <nav className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" role="tablist" aria-label="Secciones del panel">
        {visible.map((s) => (
          <button key={s.key} type="button" role="tab" aria-selected={tab === s.key} onClick={() => go(s.key)} data-testid={`admin-tab-${s.key}`}
            className={`shrink-0 px-4 min-h-[40px] rounded-full text-sm font-medium whitespace-nowrap transition-colors ${tab === s.key
              ? 'bg-gold text-navy shadow-lg shadow-gold/15'
              : 'bg-white/[0.04] text-gray-300 hover:text-white hover:bg-white/[0.06] border border-white/[0.06]'}`}>
            {s.label}
          </button>
        ))}
      </nav>

      {section.subs && !pending && (
        <div className="overflow-x-auto -mx-1 px-1">
          <Segmented tabs={section.subs.map(([k, l]) => [k, l])} value={sub} onChange={(v) => go(tab, { sub: v })} />
        </div>
      )}

      {pending ? <Loading /> : (
        <div className="min-w-0">
          {tab === 'resumen' && <AdminSummary go={go} openPerson={(key) => go('personas', { id: key })} />}
          {tab === 'personas' && (
            <PeopleSection key={params.get('nivel') || 'personas'} sub={sub} onSub={(v) => goPeople({ sub: v })}
              personKey={personKey} onOpen={openPerson} onClose={closePerson}
              eventKey={eventKey} onEvent={(v) => patch({ event: v || null })} initialTier={params.get('nivel') || ''} />
          )}
          {tab === 'eventos' && sub === 'eventos' && <EventsManager onShowRegistrations={(key) => goPeople({ sub: 'inscripciones', event: key })} />}
          {tab === 'eventos' && sub === 'bootcamps' && <BootcampManager onShowRegistrations={(key) => goPeople({ sub: 'inscripciones', event: key })} />}
          {tab === 'contenido' && sub === 'academia' && <AcademyManager />}
          {tab === 'contenido' && sub === 'recursos' && <ResourcesManager />}
          {tab === 'contenido' && sub === 'oportunidades' && <OpportunityManager />}
          {tab === 'contenido' && sub === 'textos' && <CopyEditor />}
          {tab === 'direccion' && superadmin && <CdmPanel initialSub={sub} staging={staging} />}
        </div>
      )}
    </div>
  );
}
